/**
 * Practice-app sidebar menus. Clinical roles keep a fixed list.
 * Staff / PRP logins are built from effective permissions because their
 * primary role is "staff" and access comes from assigned RBAC roles.
 */
import type { ElementType } from "react";
import {
  ArrowRightLeft,
  BarChart3,
  BrainCircuit,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CheckSquare,
  ClipboardList,
  FileInput,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Pill,
  Receipt,
  Rows3,
  Newspaper,
  Settings,
  Users,
  UsersRound,
} from "lucide-react";
import { PRP_ENTRY_ITEM } from "./prp-nav";

export interface PracticeNavItem {
  label: string;
  icon: ElementType;
  href: string;
}

export interface PracticeNavSection {
  label: string;
  items: PracticeNavItem[];
}

export type CanFn = (resource: string, action: string) => boolean;

const DASHBOARD: PracticeNavItem = {
  label: "Dashboard",
  icon: LayoutDashboard,
  href: "/dashboard",
};
const CORPORATE: PracticeNavItem = {
  label: "Corporate clients",
  icon: Building2,
  href: "/corporate",
};
const MESSAGES: PracticeNavItem = {
  label: "Messages",
  icon: MessageSquare,
  href: "/messages",
};

/** Articles, research and events the practice publishes on the Orange website. */
const COMMUNITY: PracticeNavSection = {
  label: "Community",
  items: [{ label: "Articles & events", icon: Newspaper, href: "/community" }],
};

function hasHref(sections: PracticeNavSection[], href: string): boolean {
  return sections.some((section) => section.items.some((item) => item.href === href));
}

/** Adds PRP when the user can open it and the menu does not already include it. */
export function withProgramSections(
  sections: PracticeNavSection[],
  canUsePrp: boolean,
): PracticeNavSection[] {
  if (!canUsePrp || hasHref(sections, PRP_ENTRY_ITEM.href)) return sections;
  return [...sections, { label: "Programs", items: [PRP_ENTRY_ITEM] }];
}

/** Staff / PRP: only the work this login is allowed to open. */
export function staffWorkspaceNav(can: CanFn): PracticeNavSection[] {
  const items: PracticeNavItem[] = [DASHBOARD];
  if (can("prp", "list")) items.push(PRP_ENTRY_ITEM);
  if (can("appointments", "list")) {
    items.push({ label: "Schedule", icon: Calendar, href: "/schedule" });
  }
  if (can("tasks", "list")) {
    items.push({ label: "Tasks", icon: CheckSquare, href: "/tasks" });
  }
  if (can("billing", "list")) {
    items.push({ label: "Billing", icon: Receipt, href: "/billing/bills" });
  }
  if (can("corporate_clients", "list")) items.push(CORPORATE);
  items.push(MESSAGES);

  const sections: PracticeNavSection[] = [{ label: "Workspace", items }];
  if (can("reports", "list")) {
    sections.push({
      label: "Insights",
      items: [{ label: "Analytics", icon: BarChart3, href: "/reports" }],
    });
  }
  return sections;
}

export function getNavSections(role?: string, can: CanFn = () => false): PracticeNavSection[] {
  switch (role) {
    case "super_admin":
    case "org_admin":
      return [
        {
          label: "Clinical",
          items: [
            DASHBOARD,
            { label: "Tracker", icon: Rows3, href: "/tracker" },
            { label: "Schedule", icon: Calendar, href: "/schedule" },
            { label: "Booking Requests", icon: CalendarClock, href: "/schedule/booking-requests" },
            { label: "Patients", icon: Users, href: "/patients" },
            CORPORATE,
            { label: "Forms & Intake", icon: ClipboardList, href: "/forms" },
            { label: "Tasks", icon: CheckSquare, href: "/tasks" },
            MESSAGES,
            { label: "Record Exchange", icon: ArrowRightLeft, href: "/record-exchange" },
            { label: "Received Documents", icon: FileInput, href: "/received-documents" },
          ],
        },
        {
          label: "Clinical Tools",
          items: [
            { label: "Prescriptions", icon: Pill, href: "/prescriptions" },
            { label: "Billing", icon: Receipt, href: "/billing/bills" },
          ],
        },
        {
          label: "Insights",
          items: [
            { label: "Analytics", icon: BarChart3, href: "/reports" },
            { label: "Decision Support", icon: BrainCircuit, href: "/decision-support" },
            { label: "Clinical Templates", icon: FileText, href: "/camos" },
            { label: "Therapy Groups", icon: UsersRound, href: "/groups" },
          ],
        },
        COMMUNITY,
        {
          label: "System",
          items: [{ label: "Settings", icon: Settings, href: "/settings" }],
        },
      ];

    case "physician":
    case "provider":
      return [
        {
          label: "Clinical",
          items: [
            DASHBOARD,
            { label: "Tracker", icon: Rows3, href: "/tracker" },
            { label: "My Day", icon: CalendarCheck, href: "/my-day" },
            { label: "Schedule", icon: Calendar, href: "/schedule" },
            { label: "Booking Requests", icon: CalendarClock, href: "/schedule/booking-requests" },
            { label: "Patients", icon: Users, href: "/patients" },
            CORPORATE,
            { label: "Forms & Intake", icon: ClipboardList, href: "/forms" },
            { label: "Tasks", icon: CheckSquare, href: "/tasks" },
            MESSAGES,
            { label: "Record Exchange", icon: ArrowRightLeft, href: "/record-exchange" },
            { label: "Received Documents", icon: FileInput, href: "/received-documents" },
          ],
        },
        {
          label: "Clinical Tools",
          items: [{ label: "Prescriptions", icon: Pill, href: "/prescriptions" }],
        },
        {
          label: "Insights",
          items: [
            { label: "Analytics", icon: BarChart3, href: "/reports" },
            { label: "Decision Support", icon: BrainCircuit, href: "/decision-support" },
            { label: "Clinical Templates", icon: FileText, href: "/camos" },
            { label: "Therapy Groups", icon: UsersRound, href: "/groups" },
          ],
        },
        COMMUNITY,
      ];

    case "nurse":
    case "medical_assistant":
      return [
        {
          label: "Clinical",
          items: [
            DASHBOARD,
            { label: "Tracker", icon: Rows3, href: "/tracker" },
            { label: "Schedule", icon: Calendar, href: "/schedule" },
            { label: "Patients", icon: Users, href: "/patients" },
            CORPORATE,
            { label: "Forms & Intake", icon: ClipboardList, href: "/forms" },
            { label: "Tasks", icon: CheckSquare, href: "/tasks" },
            MESSAGES,
          ],
        },
        {
          label: "Insights",
          items: [
            { label: "Decision Support", icon: BrainCircuit, href: "/decision-support" },
            { label: "Clinical Templates", icon: FileText, href: "/camos" },
            { label: "Therapy Groups", icon: UsersRound, href: "/groups" },
          ],
        },
      ];

    case "billing":
    case "billing_staff":
      return [
        {
          label: "Workspace",
          items: [DASHBOARD, MESSAGES],
        },
        {
          label: "Billing",
          items: [{ label: "Billing", icon: Receipt, href: "/billing/bills" }],
        },
        {
          label: "Insights",
          items: [{ label: "Analytics", icon: BarChart3, href: "/reports" }],
        },
      ];

    case "front_desk":
      return [
        {
          label: "Clinical",
          items: [
            DASHBOARD,
            { label: "Tracker", icon: Rows3, href: "/tracker" },
            { label: "Schedule", icon: Calendar, href: "/schedule" },
            { label: "Patients", icon: Users, href: "/patients" },
            CORPORATE,
            { label: "Forms & Intake", icon: ClipboardList, href: "/forms" },
            { label: "Tasks", icon: CheckSquare, href: "/tasks" },
            MESSAGES,
          ],
        },
        {
          label: "Front desk",
          items: [{ label: "Billing", icon: Receipt, href: "/billing/bills" }],
        },
      ];

    case "staff":
    case "prp_rc":
    case "prp_director":
      return staffWorkspaceNav(can);

    default:
      return staffWorkspaceNav(can);
  }
}
